/**
 * The workbench's cross-links live in the URL — the view, and what's in
 * focus in it — so every jump (checklist item → record → paragraph and back)
 * is linkable, survives a reload, and Back walks it.
 *
 * The record open beside the view (`record`) is kept apart: opening one
 * never moves the view, and moving the view keeps it open until it's closed.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { WORKBENCH_VIEWS, WorkbenchView } from '@/types/workbench';

/** What's in focus in a view; the view's own parameters. */
const FOCUS_PARAMS = [
  'checklist',
  'evidence',
  'para',
  'doc',
  'intervention',
  'issue',
  'file',
  'partner',
] as const;

/** The workbench's own parameters; switching case node clears them. */
export const WORKBENCH_PARAMS = ['view', ...FOCUS_PARAMS, 'record'] as const;

export interface WorkbenchFocus {
  view: WorkbenchView;
  /** A checklist item, by id. */
  checklist?: string | null;
  evidence?: string | null;
  /** A Word paragraph id (`w14:paraId`) in the document. */
  para?: string | null;
  /** The document it's in — a draft's or a version's id — where it isn't the
   * one the Document view opens on. */
  doc?: string | null;
  intervention?: string | null;
  /** A review issue in focus (Needs you's, or the Document's). */
  issue?: string | null;
  /** A file, by its key (`F1`). */
  file?: string | null;
  /** A partner's conversation in the Chat view, by partner id (null: the client's). */
  partner?: string | null;
  /** The record beside the view: kept when not given, closed by null. */
  record?: string | null;
}

export function isWorkbenchView(value: string | null): value is WorkbenchView {
  return value !== null && (WORKBENCH_VIEWS as readonly string[]).includes(value);
}

export interface WorkbenchLinks {
  view: WorkbenchView;
  checklist: string | null;
  evidence: string | null;
  para: string | null;
  doc: string | null;
  intervention: string | null;
  issue: string | null;
  file: string | null;
  partner: string | null;
  /** The record open beside the view. */
  record: string | null;
  /** Open a view, with something in focus in it — `replace` for a move
   * within a view (Needs you's next item), so Back walks views, not keys. */
  focus: (next: WorkbenchFocus, options?: { replace?: boolean }) => void;
  /** Open a record beside the view (null closes it); the view stays. */
  openRecord: (id: string | null) => void;
}

export function useWorkbenchLinks(defaultView: WorkbenchView): WorkbenchLinks {
  const [params, setParams] = useSearchParams();
  const raw = params.get('view');

  const focus = useCallback(
    (next: WorkbenchFocus, options?: { replace?: boolean }) => {
      setParams((prev) => {
        const updated = new URLSearchParams(prev);
        for (const key of ['view', ...FOCUS_PARAMS] as const) {
          updated.delete(key);
        }
        updated.set('view', next.view);
        for (const key of FOCUS_PARAMS) {
          const value = next[key];
          if (value) {
            updated.set(key, value);
          }
        }
        if (next.record !== undefined) {
          updated.delete('record');
          if (next.record) {
            updated.set('record', next.record);
          }
        }
        return updated;
      }, options);
    },
    [setParams]
  );

  const openRecord = useCallback(
    (id: string | null) => {
      setParams((prev) => {
        const updated = new URLSearchParams(prev);
        updated.delete('record');
        if (id) {
          updated.set('record', id);
        }
        return updated;
      });
    },
    [setParams]
  );

  // The same object until a link changes, so the views it's handed to
  // don't re-render with the page.
  const view = isWorkbenchView(raw) ? raw : defaultView;
  const checklist = params.get('checklist');
  const evidence = params.get('evidence');
  const para = params.get('para');
  const doc = params.get('doc');
  const intervention = params.get('intervention');
  const issue = params.get('issue');
  const file = params.get('file');
  const partner = params.get('partner');
  const record = params.get('record');
  return useMemo(
    () => ({
      view,
      checklist,
      evidence,
      para,
      doc,
      intervention,
      issue,
      file,
      partner,
      record,
      focus,
      openRecord,
    }),
    [
      view,
      checklist,
      evidence,
      para,
      doc,
      intervention,
      issue,
      file,
      partner,
      record,
      focus,
      openRecord,
    ]
  );
}
